import React, { useState, useEffect } from 'react'
import {
  CheckCircleIcon,
  XCircleIcon,
  RefreshCwIcon,
  ExternalLinkIcon,
  InfoIcon,
  FileSpreadsheetIcon,
  DownloadIcon,
  CopyIcon,
  CheckIcon,
} from 'lucide-react'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import {
  googleSheetsService,
  GoogleSheetsConfig,
} from '../../utils/googleSheets'
import { useData } from '../../contexts/DataContext'
const GOOGLE_APPS_SCRIPT_CODE = `function doPost(e) {
  try {
    var data = JSON.parse(e.postData.contents);
    var sheetName = data.sheetName || 'Citas';
    var ss = SpreadsheetApp.getActiveSpreadsheet();
    var sheet = ss.getSheetByName(sheetName);
    
    // Create sheet if it doesn't exist
    if (!sheet) {
      sheet = ss.insertSheet(sheetName);
      // Add headers
      sheet.appendRow([
        'ID', 'Paciente', 'Doctor', 'Servicio', 'Fecha', 
        'Hora', 'Duración (min)', 'Estado', 'Notas', 
        'Creado', 'Actualizado'
      ]);
      
      // Format header row
      var headerRange = sheet.getRange(1, 1, 1, 11);
      headerRange.setBackground('#4285f4');
      headerRange.setFontColor('#ffffff');
      headerRange.setFontWeight('bold');
      sheet.setFrozenRows(1);
    }
    
    if (data.action === 'test') {
      return ContentService.createTextOutput(JSON.stringify({
        success: true,
        message: 'Connection successful'
      })).setMimeType(ContentService.MimeType.JSON);
    }
    
    if (data.action === 'sync_all') {
      // Clear existing data (except headers)
      if (sheet.getLastRow() > 1) {
        sheet.deleteRows(2, sheet.getLastRow() - 1);
      }
      
      // Add all appointments
      if (data.data && data.data.length > 0) {
        var rows = data.data.map(function(apt) {
          return [
            apt.id,
            apt.patientName,
            apt.doctorName,
            apt.serviceName,
            apt.date,
            apt.time,
            apt.duration,
            apt.status,
            apt.notes,
            apt.createdAt,
            apt.updatedAt
          ];
        });
        sheet.getRange(2, 1, rows.length, 11).setValues(rows);
      }
      
      return ContentService.createTextOutput(JSON.stringify({
        success: true,
        message: 'All appointments synced'
      })).setMimeType(ContentService.MimeType.JSON);
    }
    
    if (data.action === 'create') {
      var apt = data.data;
      sheet.appendRow([
        apt.id,
        apt.patientName,
        apt.doctorName,
        apt.serviceName,
        apt.date,
        apt.time,
        apt.duration,
        apt.status,
        apt.notes,
        apt.createdAt,
        apt.updatedAt
      ]);
    }
    
    if (data.action === 'update') {
      var apt = data.data;
      var dataRange = sheet.getDataRange();
      var values = dataRange.getValues();
      
      for (var i = 1; i < values.length; i++) {
        if (values[i][0] === apt.id) {
          sheet.getRange(i + 1, 1, 1, 11).setValues([[
            apt.id,
            apt.patientName,
            apt.doctorName,
            apt.serviceName,
            apt.date,
            apt.time,
            apt.duration,
            apt.status,
            apt.notes,
            apt.createdAt,
            apt.updatedAt
          ]]);
          break;
        }
      }
    }
    
    if (data.action === 'delete') {
      var apt = data.data;
      var dataRange = sheet.getDataRange();
      var values = dataRange.getValues();
      
      for (var i = 1; i < values.length; i++) {
        if (values[i][0] === apt.id) {
          sheet.deleteRow(i + 1);
          break;
        }
      }
    }
    
    return ContentService.createTextOutput(JSON.stringify({
      success: true
    })).setMimeType(ContentService.MimeType.JSON);
    
  } catch (error) {
    return ContentService.createTextOutput(JSON.stringify({
      success: false,
      error: error.toString()
    })).setMimeType(ContentService.MimeType.JSON);
  }
}`
export function GoogleSheetsConfig() {
  const { syncAllAppointmentsToSheets, appointments } = useData()
  const [config, setConfig] = useState<GoogleSheetsConfig>({
    webAppUrl: '',
    sheetName: 'Citas',
    enabled: false,
  })
  const [isTesting, setIsTesting] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [testResult, setTestResult] = useState<{
    success: boolean
    message: string
  } | null>(null)
  const [saveMessage, setSaveMessage] = useState('')
  const [showInstructions, setShowInstructions] = useState(false)
  const [codeCopied, setCodeCopied] = useState(false)
  useEffect(() => {
    const savedConfig = googleSheetsService.getConfig()
    if (savedConfig) {
      setConfig(savedConfig)
    }
  }, [])
  const handleSave = () => {
    googleSheetsService.saveConfig(config)
    setSaveMessage('Configuración guardada correctamente')
    setTimeout(() => setSaveMessage(''), 3000)
  }
  const handleTestConnection = async () => {
    setIsTesting(true)
    setTestResult(null)
    const result = await googleSheetsService.testConnection()
    setTestResult({
      success: result.success,
      message: result.success
        ? 'Conexión exitosa con Google Sheets'
        : result.error || 'Error al conectar',
    })
    setIsTesting(false)
  }
  const handleSyncAll = async () => {
    if (!config.enabled || !config.webAppUrl) {
      setSaveMessage('Por favor configura y activa la integración primero')
      setTimeout(() => setSaveMessage(''), 3000)
      return
    }
    setIsSyncing(true)
    setSaveMessage(`Sincronizando ${appointments.length} citas...`)
    await syncAllAppointmentsToSheets()
    setSaveMessage(`✅ ${appointments.length} citas sincronizadas exitosamente`)
    setIsSyncing(false)
    setTimeout(() => setSaveMessage(''), 5000)
  }
  const handleCopyCode = () => {
    navigator.clipboard.writeText(GOOGLE_APPS_SCRIPT_CODE)
    setCodeCopied(true)
    setTimeout(() => setCodeCopied(false), 2000)
  }
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <FileSpreadsheetIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
          Integración con Google Sheets
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Sincroniza automáticamente las citas con una hoja de cálculo de Google
        </p>
      </div>

      {/* Status Card */}
      <Card
        className={`${
          config.enabled
            ? 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-green-200 dark:border-green-800'
            : 'bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-800/30 dark:to-slate-800/30 border-gray-200 dark:border-gray-700'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-lg ${
                config.enabled
                  ? 'bg-green-500 dark:bg-green-600 shadow-green-500/20'
                  : 'bg-gray-400 dark:bg-gray-600 shadow-gray-500/20'
              }`}
            >
              <FileSpreadsheetIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                {config.enabled ? 'Integración Activa' : 'Integración Inactiva'}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {config.enabled
                  ? 'Las citas se sincronizan automáticamente con Google Sheets'
                  : 'Configura la integración para comenzar a sincronizar'}
              </p>
              {config.enabled && (
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  {appointments.length} citas en el sistema
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.enabled}
                onChange={(e) =>
                  setConfig({ ...config, enabled: e.target.checked })
                }
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
            </label>
          </div>
        </div>
      </Card>

      {saveMessage && (
        <div
          className={`p-3 rounded-lg border text-sm ${
            saveMessage.includes('✅') || saveMessage.includes('correctamente')
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-600 dark:text-green-400'
              : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400'
          }`}
        >
          {saveMessage}
        </div>
      )}

      {/* Configuration Form */}
      <Card>
        <div className="space-y-4">
          <div>
            <Input
              label="URL de Google Apps Script Web App"
              value={config.webAppUrl}
              onChange={(e) =>
                setConfig({ ...config, webAppUrl: e.target.value })
              }
              placeholder="https://script.google.com/macros/s/..."
              helperText="La URL que obtienes al desplegar tu Google Apps Script como Web App"
            />
          </div>

          <div>
            <Input
              label="Nombre de la Hoja"
              value={config.sheetName}
              onChange={(e) =>
                setConfig({ ...config, sheetName: e.target.value })
              }
              placeholder="Citas"
              helperText="El nombre de la pestaña en tu hoja de cálculo donde se guardarán las citas"
            />
          </div>

          <div className="flex items-center gap-3 pt-4 flex-wrap">
            <Button onClick={handleSave}>Guardar Configuración</Button>
            <Button
              variant="secondary"
              onClick={handleTestConnection}
              isLoading={isTesting}
              disabled={!config.webAppUrl}
            >
              <RefreshCwIcon className="w-4 h-4" />
              Probar Conexión
            </Button>
            <Button
              variant="secondary"
              onClick={handleSyncAll}
              isLoading={isSyncing}
              disabled={!config.enabled || !config.webAppUrl}
            >
              <DownloadIcon className="w-4 h-4" />
              Sincronizar Todas las Citas
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowInstructions(!showInstructions)}
            >
              <InfoIcon className="w-4 h-4" />
              {showInstructions ? 'Ocultar' : 'Ver'} Instrucciones
            </Button>
          </div>

          {testResult && (
            <div
              className={`p-3 rounded-lg border flex items-start gap-2 ${
                testResult.success
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                  : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
              }`}
            >
              {testResult.success ? (
                <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className="font-medium">{testResult.message}</p>
                {testResult.success && (
                  <p className="text-sm mt-1">
                    La integración está funcionando correctamente. Las citas se
                    sincronizarán automáticamente.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Instructions */}
      {showInstructions && (
        <Card>
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Instrucciones de Configuración
            </h3>

            <div className="space-y-6 text-sm">
              {/* Step 1 */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 text-xs font-bold">
                    1
                  </span>
                  Crear una nueva hoja de cálculo
                </h4>
                <p className="ml-8 text-gray-600 dark:text-gray-400">
                  Ve a{' '}
                  <a
                    href="https://sheets.google.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 dark:text-green-400 hover:underline inline-flex items-center gap-1"
                  >
                    Google Sheets
                    <ExternalLinkIcon className="w-3 h-3" />
                  </a>{' '}
                  y crea una nueva hoja de cálculo. Nómbrala "Savidna Citas" o
                  el nombre que prefieras.
                </p>
              </div>

              {/* Step 2 */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 text-xs font-bold">
                    2
                  </span>
                  Crear un Google Apps Script
                </h4>
                <div className="ml-8 space-y-3">
                  <p className="text-gray-600 dark:text-gray-400">
                    En tu hoja, ve a <strong>Extensiones → Apps Script</strong>.
                    Borra el código existente y pega el siguiente código:
                  </p>

                  <div className="relative">
                    <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs leading-relaxed">
                      <code>{GOOGLE_APPS_SCRIPT_CODE}</code>
                    </pre>
                    <button
                      onClick={handleCopyCode}
                      className="absolute top-2 right-2 p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-2 text-xs text-gray-300"
                    >
                      {codeCopied ? (
                        <>
                          <CheckIcon className="w-4 h-4 text-green-400" />
                          <span>Copiado!</span>
                        </>
                      ) : (
                        <>
                          <CopyIcon className="w-4 h-4" />
                          <span>Copiar código</span>
                        </>
                      )}
                    </button>
                  </div>

                  <p className="text-gray-600 dark:text-gray-400">
                    Guarda el proyecto (Ctrl+S o Cmd+S) con el nombre "Savidna
                    Sync"
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 text-xs font-bold">
                    3
                  </span>
                  Desplegar como Web App
                </h4>
                <ul className="list-disc list-inside space-y-1 ml-8 text-gray-600 dark:text-gray-400">
                  <li>
                    Click en <strong>"Desplegar"</strong> (botón azul arriba a
                    la derecha)
                  </li>
                  <li>
                    Selecciona <strong>"Nueva implementación"</strong>
                  </li>
                  <li>
                    En "Tipo", selecciona <strong>"Aplicación web"</strong>
                  </li>
                  <li>Configuración:</li>
                  <ul className="list-disc list-inside ml-6 mt-1">
                    <li>
                      <strong>Ejecutar como:</strong> Yo (tu correo)
                    </li>
                    <li>
                      <strong>Quién tiene acceso:</strong> Cualquier persona
                    </li>
                  </ul>
                  <li>
                    Click en <strong>"Desplegar"</strong>
                  </li>
                  <li>Autoriza los permisos cuando se solicite</li>
                  <li>
                    <strong className="text-green-600 dark:text-green-400">
                      COPIA LA URL
                    </strong>{' '}
                    que aparece (empieza con
                    https://script.google.com/macros/s/...)
                  </li>
                </ul>
              </div>

              {/* Step 4 */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 text-xs font-bold">
                    4
                  </span>
                  Configurar en Savidna
                </h4>
                <ul className="list-disc list-inside space-y-1 ml-8 text-gray-600 dark:text-gray-400">
                  <li>Pega la URL de la Web App en el campo de arriba</li>
                  <li>Ingresa el nombre de la hoja (ej: "Citas")</li>
                  <li>Activa la integración con el toggle</li>
                  <li>
                    Click en <strong>"Guardar Configuración"</strong>
                  </li>
                  <li>
                    Click en <strong>"Probar Conexión"</strong> para verificar
                  </li>
                  <li>
                    Click en <strong>"Sincronizar Todas las Citas"</strong> para
                    sincronizar datos existentes
                  </li>
                </ul>
              </div>

              {/* Success Note */}
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <p className="text-green-700 dark:text-green-300 text-sm">
                  <strong>✅ ¡Listo!</strong> Una vez configurado, todas las
                  citas nuevas, modificadas o eliminadas se sincronizarán
                  automáticamente con tu hoja de Google Sheets en tiempo real.
                </p>
              </div>

              {/* Warning Note */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="text-amber-700 dark:text-amber-300 text-sm">
                  <strong>⚠️ Importante:</strong> Asegúrate de autorizar los
                  permisos cuando Google te lo solicite al desplegar el script.
                  El script necesita acceso para escribir en tu hoja de cálculo.
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
